(function () {

    /** The promise that the HubSpot form script will be loaded */
    let embedScriptPromise = null;

    /** The rq-ids for which a form has already been created */
    const createdFor = new Set();

    /**
     * Load the HubSpot embedded form JS
     */
    function loadHubspotEmbed(portalId) {
        const scriptSrc = `https://js.hsforms.net/forms/embed/${portalId}.js`;
        // Start loading embed script if not already loaded
        if (!embedScriptPromise) {
            embedScriptPromise = new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = scriptSrc;
                s.defer = true;
                s.onload = () => resolve();
                s.onerror = () => reject(new Error('Failed to load embed script'));
                document.head.appendChild(s);
            });
        }
        return embedScriptPromise;
    }

    /**
     * Get JSON data for the current product
     */
    function getProductJSON() {
        const el =
            document.querySelector('[id^="ProductJson-"]') ||
            document.querySelector('script[type="application/json"][data-product]') ||
            document.querySelector('script.product-json-fallback, script[type="application/json"].product-json-fallback');
        if (!el) return null;
        try {
            return JSON.parse(el.textContent || '{}');
        } catch {
            return null;
        }
    }

    /**
     * Get JSON data for the currently-selected variant
     */
    function getCurrentVariant() {
        const product = getProductJSON();
        const idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
        const vid = idInput ? Number((idInput.value || '').trim()) : null;
        if (!product || !Array.isArray(product.variants) || !vid) return null;
        return product.variants.find(v => Number(v.id) === vid) || null;
    }

    /**
     * Move the provided element into the document body
     */
    function portalToBody(el) {
        if (el && el.parentNode !== document.body) document.body.appendChild(el);
    }

    /**
     * Open the quote form modal
     */
    function openModal(modal) {
        portalToBody(modal);
        modal.classList.add('rq-open');
        modal.classList.remove('rq-hidden');
        document.documentElement.classList.add('rq-no-scroll');
        document.body.classList.add('rq-no-scroll');
        modal.querySelector('[data-rq-close]')?.focus();
    }

    /**
     * Close the quote form modal
     */
    function closeModal(modal) {
        modal.classList.remove('rq-open');
        modal.classList.add('rq-hidden');
        document.documentElement.classList.remove('rq-no-scroll');
        document.body.classList.remove('rq-no-scroll');
    }

    /**
     * Build the prefill params for the quote form
     */
    function buildPrefillParams() {
        const product = getProductJSON() || {};
        const variant = getCurrentVariant();
        const sku = variant?.sku || '';
        const cents = variant?.price ?? product.price ?? 0;
        const price = (Number(cents) / 100).toFixed(2);
        return {
            '0-1/form_submission_brand': 'CCW',
            '0-1/website': window.location.href,
            '0-1/sku': sku,
            '0-1/product_price': price
        };
    }

    /**
     * Create a HubSpot form via embed
     */
    function createViaEmbed(rqId, portalId, formId, targetEl) {
        // If already embedded, just populate the hidden fields
        if (createdFor.has(rqId)) {
            // Ensure that HubSpot forms API is available first
            if (window?.HubSpotFormsV4 && typeof window.HubSpotFormsV4 === 'object') {
                const prefill = buildPrefillParams();
                const forms = window.HubSpotFormsV4.getForms();
                forms.forEach((form) => {
                    // If the form ID matches, populated the hidden fields
                    if (form.getFormId() === formId) {
                        Object.entries(prefill).forEach(([key, value]) => {
                            form.setFieldValue(key, value);
                        });
                    }
                })
            }
            return;
        }
        // Create the form container with data attributes
        const formContainer = document.createElement('div');
        formContainer.className = 'hs-form-frame';
        formContainer.setAttribute('data-region', 'na1');
        formContainer.setAttribute('data-portal-id', portalId);
        formContainer.setAttribute('data-form-id', formId);
        // Embed the form in the target element
        targetEl.innerHTML = '';
        targetEl.appendChild(formContainer);
        // When the form is ready, populate hidden fields
        window.addEventListener('hs-form-event:on-ready', (event) => {
            const eventFormId = event.detail.formId;
            if (eventFormId === formId) {
                const prefill = buildPrefillParams();
                Object.entries(prefill).forEach(([key, value]) => {
                    HubSpotFormsV4.getFormFromEvent(event).setFieldValue(key, value);
                });
            }
        });
        // When a submission is made successfully, emit an event
        window.addEventListener('hs-form-event:on-submission:success', (event) => {
            const eventFormId = event.detail.formId;
            if (eventFormId === formId) {
                if (window.Shopify?.analytics?.publish) {
                    window.Shopify.analytics.publish('hubspot-form-success', { hsFormGuid: formId });
                }
            }
        });
        // Don't embed this form again
        createdFor.add(rqId);
    }

    // Add event listeners for opening and closing the quote form
    document.addEventListener('click', async (e) => {
        // Handle quote form open button click
        const openBtn = e.target.closest('[data-rq-open]');
        if (openBtn) {
            e.preventDefault();
            const rqId = openBtn.getAttribute('data-rq-id');
            const portalId = openBtn.getAttribute('data-portal-id');
            const formId = openBtn.getAttribute('data-form-id');
            const modal = document.querySelector(`[data-rq-modal][data-rq-id="${rqId}"]`);
            const target = document.querySelector(`[data-rq-target][data-rq-id="${rqId}"]`);
            // If the model and target were found, open the model and embed the form
            if (modal && target) {
                openModal(modal);
                await loadHubspotEmbed(portalId);
                createViaEmbed(rqId, portalId, formId, target);
            } else {
                console.error('Missing modal/target', { rqId });
            }

            return;
        }
        // Handle quote form close button click
        const closeBtn = e.target.closest('[data-rq-close]');
        if (closeBtn) {
            closeModal(closeBtn.closest('[data-rq-modal]'));
            return;
        }
        // Handle backdrop click
        const backdrop = e.target.closest('.rq-backdrop');
        if (backdrop) {
            closeModal(backdrop.closest('[data-rq-modal]'));
            return;
        }
    });

    // Handle escape key press by closing the quote form
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            document.querySelectorAll('[data-rq-modal].rq-open').forEach(closeModal);
        }
    });
})();